import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { Moedas } from 'src/app/domain/constants/moeda.constant';
import { ConciliationDto } from 'src/app/domain/models/dto/conciliation.dto';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { OrderReviewReprovedModalComponent } from '../order-review-reproved-modal/order-review-reproved-modal.component';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';

@Component({
  selector: 'app-confirm-exterior-payment',
  templateUrl: './confirm-exterior-payment.component.html',
  styleUrls: ['./confirm-exterior-payment.component.scss'],
})
export class ConfirmExteriorPaymentComponent implements OnInit, OnDestroy {
  boobleModalData: any;
  currency: any;
  moedas = Moedas;
  deme$: Observable<string[]>;

  formPayment!: FormGroup;
  formInfo!: FormGroup;
  startDate = new Date();
  loading: boolean = false;

  private readonly notifier: NotifierService;
  private destroy$ = new Subject<void>();

  constructor(
    private modalService: BoobleModalService,
    private dateAdapter: DateAdapter<any>,
    private formBuilder: FormBuilder,
    private readonly bankService: BankAccountService,
    private readonly ordersService: OrdersService,
    notifierService: NotifierService
  ) {
    this.setLocale();
    this.notifier = notifierService;

    this.formInfo = this.formBuilder.group({
      cnpj: [''],
      cpf: [''],
      receiverName: ['', Validators.required],
      currency: [
        {
          id: 1,
          value: 'USD',
          description: 'Dólar americano (USD)',
          image: 'assets/flags/usd.svg',
          icon: 'usd',
          selected: false,
        },
        Validators.required,
      ],
      senderName: ['', Validators.required],
      senderCountry: ['', Validators.required],
      senderEmail: ['', Validators.required],
    });

    this.formInfo.disable();

    this.formPayment = this.formBuilder.group({
      orderId: [null],
      amountReceived: ['', Validators.required],
      paymentDate: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      orderNumber: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      bankName: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
    });
  }

  ngOnInit() {
    if (typeof this.boobleModalData === 'number') {
      this.loading = true;
      this.ordersService
        .getOrderById(this.boobleModalData, "true")
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.loading = false;
            this.boobleModalData = result.data;
            this.initializeComponent();
          },
          error: (err) => {
            this.loading = false;
          },
        });
    } else {
      this.initializeComponent();
    }
  }

  initializeComponent() {
    this.currency = this.moedas.find(
      (moeda) => moeda.id === this.boobleModalData?.currency?.id
    );

    this.deme$ = this.bankService.getDemeBanks(this.currency.value)
      .pipe(
        map(v => v.data
          .filter(x => x.remmitanceTypes
            .includes(this.boobleModalData.remmitanceType))
              .map(x => x.bankName))
      );

    const newInfoPJ = {
      cnpj: this.boobleModalData?.corporateAccount?.cnpj ?? '',
      receiverName: this.boobleModalData?.corporateAccount?.companyName,
      currency: this.currency,
      senderName: this.boobleModalData?.sender?.companyName,
      senderCountry: this.boobleModalData?.sender?.country?.description,
      senderEmail: '',
    };

    const newInfoPF = {
      cpf: this.boobleModalData?.individualAccount?.cpf ?? '',
      receiverName: this.boobleModalData?.individualAccount?.name,
      currency: this.currency,
      senderName: this.boobleModalData?.sender?.companyName,
      senderCountry: this.boobleModalData?.sender?.country?.description,
      senderEmail: '',
    };

    this.boobleModalData.tipoPessoa == 'PJ'
      ? this.formInfo.patchValue(newInfoPJ)
      : this.formInfo.patchValue(newInfoPF);

    this.formInfo.controls['senderEmail'].patchValue(
      this.boobleModalData?.sender?.email || ''
    );
    this.formPayment.controls['orderId'].patchValue(this.boobleModalData?.id);
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  setLocale() {
    this.dateAdapter.setLocale('pt-BR');
  }

  cancelOrder(order: any) {
    this.modalService.present(OrderReviewReprovedModalComponent, {
      type: 'cancel',
      order: order,
    });
  }

  sendConfirm() {
    this.loading = true;

    this.ordersService.financialReconciliation(this.payment).subscribe({
      next: (paymentResult) => {
        this.loading = false;
        this.closeModal();
        this.notifier.notify(
          'success',
          'Pagamento no exterior confirmado com sucesso.'
        );
        this.ordersService.updateOrderView();
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formInfoControls() {
    return this.formInfo.controls;
  }

  get info(): any {
    return this.formInfo.value;
  }

  get payment(): ConciliationDto {
    return this.formPayment.value;
  }
}
